/**
 * Brands that have their own Shopify collection, by vendor.
 *
 * Each brand used to have two indexable, self-canonical URLs with the same
 * products: /collections/<handle> (in the sitemap, linked from nowhere) and
 * /collections/vendor?pf_v_brand=<VENDOR> (every internal link, in no
 * sitemap), so search signals for a brand were split between them. The
 * short one is the brand's URL now: links go there and the vendor page
 * redirects to it.
 *
 * Only collections that will always hold exactly the brand's products are
 * listed: each was checked (2026-10-05) to contain the same products as
 * the vendor page, and its smart-collection rules include
 * `vendor = <VENDOR>`, so a new product of that brand lands in it
 * automatically. Sanity's custom.brand -> collection references were not
 * used: several point elsewhere (LS2 -> helmets, R&G -> rain-gear).
 * A brand missing here keeps its vendor page.
 */
export const BRAND_COLLECTIONS: Readonly<Record<string, string>> = {
  '3M': '3m',
  ABUS: 'abus',
  'AGV ASIAN': 'agv-asian',
  AIROH: 'airoh',
  ALPINESTARS: 'alpinestars',
  'ASTRO FAMILY': 'astro-family',
  BILMOLA: 'bilmola',
  BILTWELL: 'biltwell',
  CABERG: 'caberg-1',
  CARDO: 'cardo',
  CONCRETOLOGY: 'concretology',
  CRABOO: 'craboo',
  DAINESE: 'dainese',
  DAYTONA: 'daytona',
  DEGNER: 'degner',
  'DIN MARKET': 'din-market',
  'DMD HELMET': 'meta-brand-dmd-helmet',
  DOPPELGANGER: 'doppelganger',
  EFFEX: 'effex',
  ELEVEIT: 'eleveit',
  ETHEN: 'ethen',
  'EZ COAT': 'ez-coat',
  'EZ-GO': 'ez-go',
  FACTORYEFFEX: 'factoryeffex',
  'FIVE GLOVES': 'five-gloves',
  'FLY RACING': 'fly-racing',
  FMF: 'fmf',
  FOBO: 'fobo',
  'FUCHS SILKOLENE': 'fuchs-silkolene',
  FUEL: 'fuel',
  FURYGAN: 'furygan',
  GAERNE: 'gaerne',
  GLOVETACTS: 'glovetacts',
  GOODYEAR: 'goodyear',
  'GP RACING': 'gp-racing',
  HELSTONS: 'helstons',
  HENLYBEGINS: 'henlybegins',
  HEVIK: 'hevik',
  HILX: 'hilx',
  HJC: 'hjc',
  HOLYFREEDOM: 'holyfreedom',
  'HONDA RIDING GEAR': 'honda',
  'HP HEWLETT PACKARD': 'hp-hewlett-packard',
  INSTA360: 'insta360',
  'INTUITIVE CUBE': 'intuitive-cube',
  'JOHN DOE': 'john-doe',
  JUNKSTONE: 'junkstone',
  'K&N': 'k-n',
  KABUTO: 'kabuto',
  KAPPA: 'kappa',
  KIJIMA: 'kijima',
  KITACO: 'kitaco',
  KOMINE: 'komine',
  KRIEGA: 'kriega',
  KUSHITANI: 'kushitani',
  LBB: 'lbb',
  'LS2 HELMETS': 'ls2-helmets',
  LUBRO: 'lubro',
  'MECHANIX WEAR': 'mechanix-wear',
  MITSUBA: 'mitsuba',
  MODER: 'moder',
  MONGOLIAN: 'mongolian',
  'MUC-OFF': 'muc-off',
  MUFU: 'mufu',
  MWUPP: 'mwupp',
  NEXX: 'brand-nexx-1',
  NOLAN: 'nolan',
  OUTPERFORM: 'outperform',
  PAKELO: 'pakelo',
  PANDO: 'pando',
  PEAKDESIGN: 'peakdesign',
  POLAROID: 'polaroid',
  PUTOLINE: 'putoline',
  QUADLOCK: 'quadlock',
  'R&G': 'r-g',
  'RED K': 'red-k',
  'ROUGH AND ROAD': 'rough-and-road',
  'RS TAICHI': 'rs-taichi',
  SBS: 'sbs',
  SCORPION: 'scorpion',
  SCOYCO: 'scoyco',
  SHOEI: 'shoei',
  SIDI: 'sidi',
  'SPHERE LIGHT': 'sphere-light',
  SPIDI: 'spidi',
  TAKEGAWA: 'takegawa',
  TCX: 'tcx',
  TEOGONIA: 'teogonia',
  'W!ZH': 'w-zh',
  WENTWORTH: 'wentworth',
  'X-LITE': 'x-lite',
  XILLA: 'xilla',
  XPD: 'xpd',
  YOSHIMURA: 'yoshimura',
};

const BRAND_BY_HANDLE: ReadonlyMap<string, string> = new Map(
  Object.entries(BRAND_COLLECTIONS).map(([vendor, handle]) => [handle, vendor]),
);

/** Where a link to this brand should go (locale prefix added by <Link>). */
export function brandHref(vendor?: string | null): string {
  if (!vendor) return '/pages/brands';
  const handle = BRAND_COLLECTIONS[vendor];
  return handle
    ? `/collections/${handle}`
    : `/collections/vendor?${new URLSearchParams({pf_v_brand: vendor})}`;
}

/** The vendor a brand collection stands for, or undefined. */
export function brandForCollection(handle?: string | null) {
  return handle ? BRAND_BY_HANDLE.get(handle) : undefined;
}
